import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Settings2, BarChart2, Activity } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../../components/Toast';
import apiService from '../../services/apiService';
import StrategyBuilderModal from './StrategyBuilderModal';
import BacktestChart from './BacktestChart';

const Container = styled.div`
  min-height: 100vh;
  background: #0f0f0f;
  color: #e5e5e5;
  padding: 20px;
  font-family: 'Inter', sans-serif;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #888;
  cursor: pointer;
  padding: 8px;
  &:hover { color: #fff; }
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 24px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 12px;
  padding: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 13px;
  color: #aaa;
  font-weight: 500;
`;

const Input = styled.input`
  background: #222;
  border: 1px solid #444;
  color: #fff;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 14px;
  outline: none;
  &:focus { border-color: #0072ff; }
`;

const Select = styled.select`
  background: #222;
  border: 1px solid #444;
  color: #fff;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 14px;
  outline: none;
  &:focus { border-color: #0072ff; }
`;

const RunButton = styled.button`
  background: linear-gradient(135deg, #0072ff, #00c6ff);
  color: #fff;
  border: none;
  padding: 12px;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  margin-top: 24px;
  opacity: ${props => props.disabled ? 0.7 : 1};

  &:hover {
    opacity: 0.9;
  }
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  margin: 0 0 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #333;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ResultCard = styled.div`
  background: #222;
  border: 1px solid ${props => props.isBest ? '#0072ff' : '#333'};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  position: relative;
`;

const BestBadge = styled.div`
  position: absolute;
  top: -10px;
  right: 16px;
  background: #0072ff;
  color: white;
  font-size: 11px;
  font-weight: bold;
  padding: 4px 8px;
  border-radius: 12px;
`;

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-top: 12px;
`;

const Metric = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  span {
    font-size: 11px;
    color: #888;
  }
  strong {
    font-size: 16px;
    color: ${props => props.color || '#fff'};
  }
`;

const ParamTag = styled.span`
  background: #333;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  margin-right: 8px;
  color: #bbb;
`;

const TradeLogTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  margin-top: 16px;

  th, td {
    padding: 8px;
    text-align: right;
    border-bottom: 1px solid #333;
  }
  th {
    color: #888;
    font-weight: 500;
  }
  td:first-child, th:first-child { text-align: left; }
`;

const SearchDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background: #222;
  border: 1px solid #444;
  border-radius: 4px;
  margin-top: 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 50;
  box-shadow: 0 4px 12px rgba(0,0,0,0.5);
`;

const SearchHistoryItem = styled.div`
  padding: 8px 12px;
  font-size: 13px;
  color: #ddd;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  &:hover {
    background: #333;
  }
`;

const QuickSelectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  margin-bottom: 8px;
`;

const QuickSelectBtn = styled.button`
  background: #333;
  border: 1px solid #444;
  color: #ccc;
  padding: 6px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    background: #444;
    color: #fff;
  }
`;

const Button = styled.button`
  background: #0072ff;
  color: #fff;
  font-weight: 500;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:hover {
    background: #005bb5;
  }
`;

const getBaseUrl = () => {
    return (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
        ? 'http://localhost:5000'
        : window.location.origin;
};

function BacktestPage() {
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [symbol, setSymbol] = useState('005930'); // Default to Samsung Electronics Code
    const [period, setPeriod] = useState('D');
    const [strategy, setStrategy] = useState('TBM');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [maxCandles, setMaxCandles] = useState('');

    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState(null);

    const [customStrategies, setCustomStrategies] = useState([]);
    const [isBuilderOpen, setIsBuilderOpen] = useState(false);

    // Search State
    const [searchQuery, setSearchQuery] = useState('삼성전자');
    const [isSearching, setIsSearching] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [isSearchingApi, setIsSearchingApi] = useState(false);

    // Live Search Effect (Debounced)
    React.useEffect(() => {
        const fetchSearchResults = async () => {
            if (!searchQuery.trim()) {
                setSearchResults([]);
                return;
            }
            setIsSearchingApi(true);
            try {
                const results = await apiService.searchStocks(searchQuery.trim());
                setSearchResults(results || []);
            } catch (err) {
                console.error('Search API error:', err);
                setSearchResults([]);
            } finally {
                setIsSearchingApi(false);
            }
        };

        const delayDebounceFn = setTimeout(() => {
            fetchSearchResults();
        }, 100);

        return () => clearTimeout(delayDebounceFn);
    }, [searchQuery]);

    // Fetch Custom Strategies
    React.useEffect(() => {
        const fetchStrategies = async () => {
            try {
                const res = await axios.get(`${getBaseUrl()}/api/stock/backtest/strategies`);
                setCustomStrategies(res.data || []);
            } catch (err) {
                console.error('Failed to fetch strategies:', err);
            }
        };
        fetchStrategies();
    }, []);

    // Format Date Helper: YYYY-MM-DD
    const formatDate = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    // Quick Select Date Range Handler
    const setQuickDateRange = (monthsAgo) => {
        setMaxCandles('');
        if (monthsAgo === 'ALL') {
            setStartDate('');
            setEndDate('');
            return;
        }

        const today = new Date();
        const past = new Date();
        past.setMonth(today.getMonth() - monthsAgo);

        setStartDate(formatDate(past));
        setEndDate(formatDate(today));
    };

    const setExactCandles = (count) => {
        setStartDate('');
        setEndDate('');
        setMaxCandles(count);
    };

    const handleRun = async () => {
        if (!symbol.trim()) {
            showToast("종목을 입력해주세요.", "error");
            return;
        }

        setLoading(true);
        setResults(null);

        // Validate dates if both provided
        if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
            showToast("시작일이 종료일보다 늦을 수 없습니다.", "error");
            return;
        }

        // Define Grid Search Param Ranges locally for now
        let paramRanges = {};
        if (strategy === 'TBM') {
            paramRanges = {
                period: [7, 10, 14, 20],
                multiplier: [2, 2.5, 3, 3.5]
            };
        } else if (strategy === 'VOLUME_MA') {
            paramRanges = {
                maPeriod: [5, 10, 20],
                volumeMultiplier: [1.5, 2, 3]
            };
        } else if (strategy === 'REVERSAL') {
            paramRanges = {
                rsiPeriod: [9, 14, 20],
                rsiOversold: [25, 30, 35]
            };
        } else if (strategy === 'MACD_RSI') {
            paramRanges = {
                fastPeriod: [8, 12],
                slowPeriod: [21, 26],
                rsiPeriod: [10, 14],
            };
        } else if (strategy === 'BB_BREAKOUT') {
            paramRanges = {
                bbPeriod: [15, 20, 25],
                bbStdDev: [1.5, 2, 2.5],
            };
        } else if (strategy === 'TRIPLE_MA') {
            paramRanges = {
                fastPeriod: [3, 5, 10],
                midPeriod: [15, 20, 30],
                slowPeriod: [50, 60],
            };
        }

        // Identify if selected strategy is custom
        const selectedCustom = customStrategies.find(s => s.id === strategy);
        let finalParamRanges = paramRanges;

        // If it's a custom strategy, for now we will just pass an empty paramRanges 
        // because parameter optimization for custom ones needs more logic, 
        // but the backend handles `[{}]` correctly if paramRanges is empty.
        if (selectedCustom) {
            finalParamRanges = { default: [1] }; // Dummy range to force 1 iteration
        }

        try {
            const res = await axios.post(`${getBaseUrl()}/api/stock/backtest/optimize`, {
                symbol: symbol.trim(),
                period,
                strategy,
                paramRanges: finalParamRanges,
                startDate,
                endDate,
                maxCandles
            });
            setResults(res.data);
        } catch (err) {
            console.error(err);
            showToast(err.response?.data?.error || "백테스트 실패", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleCopyJson = () => {
        if (!results || !results.topResults || results.topResults.length === 0) {
            showToast('복사할 데이터가 없습니다.', 'error');
            return;
        }

        // Export just the best result which has the chart data attached
        const bestResult = results.topResults[0];

        try {
            const jsonStr = JSON.stringify(bestResult, null, 2);
            navigator.clipboard.writeText(jsonStr).then(() => {
                showToast('차트 데이터가 JSON 형식으로 클립보드에 복사되었습니다.', 'success');
            }).catch(err => {
                console.error('Clipboard copy failed:', err);
                showToast('클립보드 복사 실패', 'error');
            });
        } catch (err) {
            console.error('JSON stringify failed:', err);
            showToast('데이터 변환 중 오류가 발생했습니다.', 'error');
        }
    };

    const handleColorChange = async (indId, newColor) => {
        const selectedCustom = customStrategies.find(s => s.id === strategy);
        if (!selectedCustom) return; // Only save colors for custom strategies

        // 1. Update the local custom strategies state eagerly
        const updatedIndicators = selectedCustom.indicators.map(ind => 
            ind.id === indId ? { ...ind, color: newColor } : ind
        );
        const updatedStrategy = { ...selectedCustom, indicators: updatedIndicators, updatedAt: new Date().toISOString() };
        
        setCustomStrategies(prev => prev.map(s => s.id === strategy ? updatedStrategy : s));

        // 2. Update the results state so if the chart is resized/re-rendered it holds the new color configuration
        if (results && results.topResults && results.topResults[0] && results.topResults[0].chartData) {
            const chartData = results.topResults[0].chartData;
            if (chartData.indicatorDefs) {
                chartData.indicatorDefs = chartData.indicatorDefs.map(def => 
                    def.id === indId ? { ...def, color: newColor } : def
                );
            }
            // Trigger shallow re-render without breaking chart instance
            setResults({ ...results }); 
        }

        // 3. Persist to DB
        try {
            await axios.post(`${getBaseUrl()}/api/stock/backtest/strategies`, updatedStrategy);
        } catch (err) {
            console.error('Failed to save color preference:', err);
            showToast('색상 저장 실패', 'error');
        }
    };

    return (
        <Container>
            <TopBar>
                <BackButton onClick={() => navigate('/labs')}><ArrowLeft /></BackButton>
                <Title><Activity color="#0072ff" /> Backtest Optimizer</Title>
            </TopBar>

            <Layout>
                {/* LEFT: Settings Panel */}
                <Panel>
                    <SectionTitle><Settings2 size={16} /> 설정</SectionTitle>

                    <FormGroup style={{ position: 'relative' }}>
                        <Label>
                            종목명 또는 코드
                            <span style={{ color: '#0072ff', marginLeft: 8, fontSize: '11px' }}>{symbol}</span>
                        </Label>
                        <Input
                            value={searchQuery}
                            onFocus={() => setIsSearching(true)}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setIsSearching(true);
                            }}
                            onBlur={() => setTimeout(() => setIsSearching(false), 200)}
                            placeholder="종목명 입력 후 선택"
                            autoComplete="off"
                        />
                        {isSearching && (
                            <SearchDropdown>
                                {searchQuery.trim() ? (
                                    <>
                                        {isSearchingApi ? (
                                            <div style={{ padding: '8px 12px', fontSize: '13px', color: '#888' }}>검색 중...</div>
                                        ) : searchResults.length > 0 ? (
                                            searchResults.map((item, idx) => (
                                                <SearchHistoryItem key={item.code || idx} onMouseDown={() => {
                                                    setSymbol(item.code);
                                                    setSearchQuery(item.name);
                                                    setIsSearching(false);
                                                }}>
                                                    <span>{item.name} <span style={{ fontSize: '11px', color: '#777' }}>{item.code}</span></span>
                                                </SearchHistoryItem>
                                            ))
                                        ) : (
                                            <div style={{ padding: '8px 12px', fontSize: '13px', color: '#888' }}>결과 없음</div>
                                        )}
                                    </>
                                ) : (
                                    <div style={{ padding: '8px 12px', fontSize: '12px', color: '#888' }}>
                                        종목명을 입력하세요 (예: 현대차)
                                    </div>
                                )}
                            </SearchDropdown>
                        )}
                    </FormGroup>

                    <FormGroup>
                        <Label>테스트 주기</Label>
                        <Select value={period} onChange={e => setPeriod(e.target.value)}>
                            <option value="D">일봉 (Daily)</option>
                            <option value="W">주봉 (Weekly)</option>
                        </Select>
                    </FormGroup>

                    <FormGroup>
                        <Label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>최적화 전략 모델</span>
                            <button
                                onClick={() => setIsBuilderOpen(true)}
                                style={{ background: '#0072ff', color: '#fff', border: 'none', borderRadius: '4px', padding: '4px 8px', fontSize: '11px', cursor: 'pointer' }}
                            >
                                + 전략 관리/추가
                            </button>
                        </Label>
                        <Select value={strategy} onChange={e => setStrategy(e.target.value)}>
                            <option value="TBM">TBM SuperTrend 추세추종</option>
                            <option value="VOLUME_MA">Volume + MA 돌파</option>
                            <option value="REVERSAL">복합 추세 반전 (RSI+MACD)</option>
                            <option value="MACD_RSI">MACD + RSI 크로스 필터</option>
                            <option value="BB_BREAKOUT">볼린저 밴드 돌파</option>
                            <option value="TRIPLE_MA">삼중 이동평균 교차</option>
                            {customStrategies.map(s => (
                                <option key={s.id} value={s.id}>[커스텀] {s.name}</option>
                            ))}
                        </Select>
                    </FormGroup>

                    <FormGroup>
                        <Label>테스트 기간 (옵션)</Label>
                        <QuickSelectGrid>
                            <QuickSelectBtn onClick={() => setQuickDateRange(1)}>최근 1개월</QuickSelectBtn>
                            <QuickSelectBtn onClick={() => setQuickDateRange(3)}>최근 3개월</QuickSelectBtn>
                            <QuickSelectBtn onClick={() => setQuickDateRange(12)}>최근 1년</QuickSelectBtn>
                            <QuickSelectBtn onClick={() => setExactCandles(1000)}>1000봉(약4년)</QuickSelectBtn>
                            <QuickSelectBtn onClick={() => setQuickDateRange('ALL')}>전체 데이터</QuickSelectBtn>
                        </QuickSelectGrid>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <Input
                                type="date"
                                value={startDate}
                                onChange={e => { setStartDate(e.target.value); setMaxCandles(''); }}
                                style={{ flex: 1, color: startDate ? '#fff' : '#888' }}
                            />
                            <span style={{ color: '#888', alignSelf: 'center' }}>~</span>
                            <Input
                                type="date"
                                value={endDate}
                                onChange={e => { setEndDate(e.target.value); setMaxCandles(''); }}
                                style={{ flex: 1, color: endDate ? '#fff' : '#888' }}
                            />
                        </div>
                        <span style={{ fontSize: '10px', color: maxCandles ? '#ffeb3b' : '#666', marginTop: '4px' }}>
                            {maxCandles ? `선택됨: 가장 최근 ${maxCandles}개의 캔들만 분석명령 인가` : '미지정 시 전체 수집 데이터를 대상으로 합니다.'}
                        </span>
                    </FormGroup>

                    <RunButton onClick={handleRun} disabled={loading}>
                        {loading ? '최적화 연산 중...' : <><Play size={16} /> 백테스트 즉시 실행</>}
                    </RunButton>
                </Panel>

                {/* RIGHT: Results Panel */}
                <Panel>
                    <SectionTitle><BarChart2 size={16} /> 최적화 결과 Top 3</SectionTitle>

                    {!results && !loading && (
                        <div style={{ color: '#888', textAlign: 'center', padding: '60px 0' }}>
                            좌측에서 설정을 완료하고 실행 버튼을 눌러주세요.
                        </div>
                    )}

                    {loading && (
                        <div style={{ color: '#0072ff', textAlign: 'center', padding: '60px 0', fontWeight: 'bold' }}>
                            서버에서 수십 개의 파라미터 조합을 시뮬레이션 하고 있습니다...
                        </div>
                    )}

                    {results && results.topResults && results.topResults.map((res, idx) => (
                        <ResultCard key={idx} isBest={idx === 0} style={{ position: 'relative' }}>
                            {idx === 0 && <BestBadge>Best Pick</BestBadge>}

                            {/* Export Data Button (Moved to top right) */}
                            {idx === 0 && (
                                <Button
                                    style={{
                                        position: 'absolute',
                                        top: '12px',
                                        right: '12px',
                                        background: '#333',
                                        fontSize: '11px',
                                        padding: '4px 8px',
                                        color: '#ccc'
                                    }}
                                    onClick={handleCopyJson}
                                    title="전체 타점/보조지표/차트데이터 추출"
                                >
                                    JSON 추출
                                </Button>
                            )}

                            <div style={{ marginBottom: '12px', marginTop: idx === 0 ? '30px' : '0' }}>
                                {Object.entries(res.params).map(([k, v]) => (
                                    <ParamTag key={k}>{k}: <b>{v}</b></ParamTag>
                                ))}
                            </div>

                            <MetricGrid>
                                <Metric color={parseFloat(res.totalReturn) > 0 ? '#00e676' : '#ff4ea1'}>
                                    <span>누적 수익률</span>
                                    <strong>{res.totalReturn}%</strong>
                                </Metric>
                                <Metric>
                                    <span>승률</span>
                                    <strong>{res.winRate}%</strong>
                                </Metric>
                                <Metric color="#ff4ea1">
                                    <span>최대 낙폭 (MDD)</span>
                                    <strong>{res.maxDrawdown}%</strong>
                                </Metric>
                                <Metric>
                                    <span>매매 횟수</span>
                                    <strong>{res.totalTrades} 회</strong>
                                </Metric>
                            </MetricGrid>

                            {/* Show trade log only for the Best Pick for brevity */}
                            {idx === 0 && res.tradeLog && res.tradeLog.length > 0 && (
                                <details style={{ marginTop: '16px' }}>
                                    <summary style={{ fontSize: '12px', color: '#0072ff', cursor: 'pointer', outline: 'none' }}>
                                        최적 파라미터 매매 상세 내역 ({res.tradeLog.length}건) 보기
                                    </summary>
                                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                        <TradeLogTable>
                                            <thead>
                                                <tr>
                                                    <th>유형</th>
                                                    <th>일자</th>
                                                    <th>체결가</th>
                                                    <th>수량</th>
                                                    <th>손익/잔고</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {res.tradeLog.map((log, lIdx) => (
                                                    <tr key={lIdx}>
                                                        <td style={{ color: log.type === 'BUY' ? '#ff4ea1' : '#00e676', fontWeight: 'bold' }}>
                                                            {log.type}
                                                        </td>
                                                        <td>{String(log.date).replace(/(\d{4})(\d{2})(\d{2})/, "$1-$2-$3")}</td>
                                                        <td>{log.price.toLocaleString()}원</td>
                                                        <td>{log.quantity}주</td>
                                                        <td>
                                                            {log.type === 'SELL' ? (
                                                                <span style={{ color: log.profit > 0 ? '#00e676' : '#ff4ea1' }}>
                                                                    {log.profit > 0 ? '+' : ''}{Math.round(log.profit).toLocaleString()}원
                                                                </span>
                                                            ) : (
                                                                <span style={{ color: '#888' }}>{Math.round(log.balance).toLocaleString()}원</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </TradeLogTable>
                                    </div>
                                </details>
                            )}

                            {/* Backtest Chart for Best Pick */}
                            {idx === 0 && res.chartData && res.chartData.candles && (
                                <div style={{ marginTop: '24px' }}>
                                    <h5 style={{ margin: '0 0 8px 0', color: '#0072ff', fontSize: '13px' }}>
                                        <BarChart2 size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />
                                        시뮬레이션 차트 및 매매 타점
                                    </h5>
                                    <BacktestChart chartData={res.chartData} onColorChange={handleColorChange} />
                                </div>
                            )}
                        </ResultCard>
                    ))}
                </Panel>
            </Layout>

            {/* Strategy Builder Modal */}
            {isBuilderOpen && (
                <StrategyBuilderModal
                    onClose={() => setIsBuilderOpen(false)}
                    customStrategies={customStrategies}
                    onSaveComplete={() => {
                        const fetchStrategies = async () => {
                            try {
                                const res = await axios.get(`${getBaseUrl()}/api/stock/backtest/strategies`);
                                setCustomStrategies(res.data || []);
                            } catch (err) {
                                console.error('Failed to fetch strategies:', err);
                            }
                        };
                        fetchStrategies();
                    }}
                />
            )}
        </Container>
    );
}

export default BacktestPage;
